import {MarketplaceFilters} from '../../types/marketplace/MarketplaceFilesRequest';
import {IntegrationPurpose} from '../../types/integrations/IntegrationResponse';
import {createIntl, createIntlCache} from 'react-intl';
import messages from '../en.json';

const cache = createIntlCache();
const intl = createIntl({locale: navigator.language, messages: messages}, cache);

type MarketplaceFlagFilter = {
  key: keyof Pick<MarketplaceFilters, 'compatibleOnly'>;
  type: 'FLAG';
  limitBy: IntegrationPurpose[];
  label: string;
};

type MarketplaceMultipleFilter = {
  key: keyof Pick<MarketplaceFilters, 'studioVersion'>;
  label: string;
  limitBy: IntegrationPurpose[];
  type: 'MULTIPLE';
  options: FilterOptions;
  tooltip?: string;
};

type MarketplaceOptionsFilter = {
  key: keyof Pick<
    MarketplaceFilters,
    | 'purpose'
    | 'dataLoader'
    | 'colorSpace'
    | 'type'
    | 'baseModel'
    | 'yoloType'
    | 'applicationType'
    | 'transformationType'
    | 'executionContainer'
    | 'postProcessorType'
  >;
  type: 'OPTIONS';
  limitBy: IntegrationPurpose[];
  label: string;
  options: FilterOptions;
  tooltip?: string;
};

type MarketplaceTagsFilter = {
  key: keyof Pick<MarketplaceFilters, 'tags' | 'labelNames'>;
  type: 'TAGS';
  limitBy: IntegrationPurpose[];
  label: string;
  tooltip?: string;
};

type MarketplaceCustomFilter = {
  key: keyof Pick<MarketplaceFilters, 'categoryName'>;
  label: string;
  limitBy: IntegrationPurpose[];
  type: 'CUSTOM';
};

export type FilterDefinitions = Array<
  | MarketplaceFlagFilter
  | MarketplaceOptionsFilter
  | MarketplaceMultipleFilter
  | MarketplaceTagsFilter
  | MarketplaceCustomFilter
>;

class FilterOptions {
  values: Record<string, string> = {};
  order: string[] = [];

  constructor(elements: FilterOption[]) {
    elements.forEach(({key, value}) => {
      this.values[key] = intl.formatMessage({id: value});
      this.order.push(key);
    });
  }

  getValues(): Record<string, string> {
    return this.values;
  }

  getOrder(): string[] {
    return this.order;
  }

  getValue(key: string): string | undefined {
    return this.values[key];
  }

  getLength(): number {
    return this.order.length;
  }
}

type FilterOption = {
  key: string;
  value: string;
};

export const filterDefinitions: FilterDefinitions = [
  {
    key: 'compatibleOnly',
    type: 'FLAG',
    limitBy: ['DATASET_STORAGE'],
    label: 'marketplaceResult.showCompatibleDatasets',
  },
  {
    key: 'compatibleOnly',
    type: 'FLAG',
    limitBy: ['MODEL_STORAGE'],
    label: 'marketplaceResult.showCompatibleModels',
  },
  {
    key: 'compatibleOnly',
    type: 'FLAG',
    limitBy: ['ISP_STORAGE'],
    label: 'marketplaceResult.showCompatiblePreProcessors',
  },
  {
    key: 'compatibleOnly',
    type: 'FLAG',
    limitBy: ['PP_STORAGE'],
    label: 'marketplaceResult.showCompatiblePostProcessors',
  },
  {
    key: 'purpose',
    type: 'OPTIONS',
    limitBy: ['DATASET_STORAGE', 'MODEL_STORAGE', 'SOLUTION_STORAGE'],
    label: 'marketplaceResult.purpose',
    options: new FilterOptions([
      {
        key: 'Classification',
        value: 'marketplaceExplorer.filterDefinitions.purpose.option.classification',
      },
      {
        key: 'Detection',
        value: 'marketplaceExplorer.filterDefinitions.purpose.option.detection',
      },
      {
        key: 'PoseDetection',
        value: 'marketplaceExplorer.filterDefinitions.purpose.option.poseDetection',
      },
      {
        key: 'Segmentation',
        value: 'marketplaceExplorer.filterDefinitions.purpose.option.segmentation',
      },
      {key: 'Other', value: 'marketplaceExplorer.filterDefinitions.purpose.option.other'},
    ]),
    tooltip: intl.formatMessage({
      id: 'marketplaceExplorer.filterDefinitions.purpose.tooltip',
    }),
  },
  {
    key: 'dataLoader',
    type: 'OPTIONS',
    limitBy: ['DATASET_STORAGE'],
    label: 'marketplaceResult.dataLoader',
    options: new FilterOptions([
      {
        key: 'Custom',
        value: 'marketplaceExplorer.filterDefinitions.dataLoader.option.custom',
      },
      {
        key: 'ImageFolder',
        value: 'marketplaceExplorer.filterDefinitions.dataLoader.option.imageFolder',
      },
      {
        key: 'COCO',
        value: 'marketplaceExplorer.filterDefinitions.dataLoader.option.coco',
      },
    ]),
    tooltip: intl.formatMessage({
      id: 'marketplaceExplorer.filterDefinitions.dataLoader.tooltip',
    }),
  },
  {
    key: 'colorSpace',
    type: 'OPTIONS',
    limitBy: ['DATASET_STORAGE'],
    label: 'marketplaceResult.colorSpace',
    options: new FilterOptions([
      {
        key: 'bayer_rggb',
        value: 'marketplaceExplorer.filterDefinitions.colorSpace.option.bayerRggb',
      },
      {
        key: 'bayer_bggr',
        value: 'marketplaceExplorer.filterDefinitions.colorSpace.option.bayerBggr',
      },
      {
        key: 'bayer_rgbg',
        value: 'marketplaceExplorer.filterDefinitions.colorSpace.option.bayerRgbg',
      },
      {
        key: 'bayer_grgb',
        value: 'marketplaceExplorer.filterDefinitions.colorSpace.option.bayerGrgb',
      },
      {key: 'rgb', value: 'marketplaceExplorer.filterDefinitions.colorSpace.option.rgb'},
      {key: 'bgr', value: 'marketplaceExplorer.filterDefinitions.colorSpace.option.bgr'},
    ]),
    tooltip: intl.formatMessage({
      id: 'marketplaceExplorer.filterDefinitions.colorSpace.tooltip',
    }),
  },
  {
    key: 'labelNames',
    type: 'TAGS',
    limitBy: ['DATASET_STORAGE'],
    label: 'marketplaceResult.labelsFilter',
    tooltip: intl.formatMessage({
      id: 'marketplaceExplorer.filterDefinitions.labels.tooltip',
    }),
  },
  {
    key: 'type',
    type: 'OPTIONS',
    limitBy: ['MODEL_STORAGE'],
    label: 'marketplaceResult.type',
    options: new FilterOptions([
      {key: 'CAFFE_2', value: 'marketplaceExplorer.filterDefinitions.type.option.caffe2'},
      {
        key: 'PY_TORCH',
        value: 'marketplaceExplorer.filterDefinitions.type.option.pyTorch',
      },
      {
        key: 'TENSOR_FLOW',
        value: 'marketplaceExplorer.filterDefinitions.type.option.tensorFlow',
      },
      {
        key: 'PY_PICASSO',
        value: 'marketplaceExplorer.filterDefinitions.type.option.pyAct',
      },
      {key: 'ONNX', value: 'marketplaceExplorer.filterDefinitions.type.option.onnx'},
      {
        key: 'OPENCV',
        value: 'marketplaceExplorer.filterDefinitions.type.option.pythonOpenCv',
      },
      {
        key: 'CVEDIA_RT',
        value: 'marketplaceExplorer.filterDefinitions.type.option.cvediaRt',
      },
    ]),
  },
  {
    key: 'baseModel',
    type: 'OPTIONS',
    limitBy: ['MODEL_STORAGE'],
    label: 'marketplaceResult.baseModel',
    options: new FilterOptions([
      {
        key: 'YOLOV3',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.yolov3',
      },
      {
        key: 'YOLOV3_TINY',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.yolov3tiny',
      },
      {
        key: 'YOLOV4',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.yolov4',
      },
      {
        key: 'YOLOV5',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.yolov5',
      },
      {
        key: 'YOLOV5V7',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.yolov5v7',
      },
      {
        key: 'YOLOV8',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.yolov8',
      },
      {
        key: 'GOOGLENET',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.googlenet',
      },
      {
        key: 'MOBILENET',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.mobilenet',
      },
      {
        key: 'RESNET18',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.resnet18',
      },
      {
        key: 'RESNET34',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.resnet34',
      },
      {
        key: 'RESNET50',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.resnet50',
      },
      {
        key: 'RESNET101',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.resnet101',
      },
      {
        key: 'RESNET152',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.resnet152',
      },
      {
        key: 'RESNEXT50',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.resnext50',
      },
      {
        key: 'SQUEEZENET',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.squeezenet',
      },
      {
        key: 'FER_PLUS',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.ferplus',
      },
      {
        key: 'VGG11',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.vgg11',
      },
      {
        key: 'VGG13',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.vgg13',
      },
      {
        key: 'VGG16',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.vgg16',
      },
      {
        key: 'VGG19',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.vgg19',
      },
      {key: 'UNET', value: 'marketplaceExplorer.filterDefinitions.baseModel.option.unet'},
      {
        key: 'SWIFTNET',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.swiftnet',
      },
      {
        key: 'OPENPOSE',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.openpose',
      },
      {
        key: 'POSENET',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.posenet',
      },
      {
        key: 'OTHER',
        value: 'marketplaceExplorer.filterDefinitions.baseModel.option.other',
      },
    ]),
  },
  {
    key: 'yoloType',
    type: 'OPTIONS',
    limitBy: ['MODEL_STORAGE'],
    label: 'marketplaceResult.modelVariant',
    options: new FilterOptions([
      {
        key: 'YOLOV5N',
        value: 'marketplaceExplorer.filterDefinitions.modelVariant.option.yoloV5Nano',
      },
      {
        key: 'YOLOV5S',
        value: 'marketplaceExplorer.filterDefinitions.modelVariant.option.yoloV5Small',
      },
      {
        key: 'YOLOV5M',
        value: 'marketplaceExplorer.filterDefinitions.modelVariant.option.yoloV5Medium',
      },
      {
        key: 'YOLOV5L',
        value: 'marketplaceExplorer.filterDefinitions.modelVariant.option.yoloV5Large',
      },
      {
        key: 'YOLOV5X',
        value:
          'marketplaceExplorer.filterDefinitions.modelVariant.option.yoloV5ExtraLarge',
      },
    ]),
  },
  {
    key: 'categoryName',
    label: 'marketplaceResult.category',
    type: 'CUSTOM',
    limitBy: ['SOLUTION_STORAGE'],
  },
  {
    key: 'transformationType',
    type: 'OPTIONS',
    limitBy: ['ISP_STORAGE'],
    label: 'marketplaceResult.preProcessorType',
    options: new FilterOptions([
      {
        key: 'ISP',
        value: 'marketplaceExplorer.filterDefinitions.transformationType.option.isp',
      },
      {
        key: 'SCRIPT',
        value: 'marketplaceExplorer.filterDefinitions.transformationType.option.script',
      },
    ]),
    tooltip: intl.formatMessage({
      id: 'marketplaceExplorer.filterDefinitions.processorType.tooltip',
    }),
  },
  {
    key: 'postProcessorType',
    type: 'OPTIONS',
    limitBy: ['PP_STORAGE'],
    label: 'marketplaceResult.postProcessorType',
    options: new FilterOptions([
      {
        key: 'MODEL',
        value: 'marketplaceExplorer.filterDefinitions.postProcessorType.option.model',
      },
      {
        key: 'APPLICATION',
        value:
          'marketplaceExplorer.filterDefinitions.postProcessorType.option.application',
      },
      {
        key: 'VALIDATION',
        value:
          'marketplaceExplorer.filterDefinitions.postProcessorType.option.validation',
      },
    ]),
    tooltip: intl.formatMessage({
      id: 'marketplaceExplorer.filterDefinitions.processorType.tooltip',
    }),
  },
  {
    key: 'applicationType',
    type: 'OPTIONS',
    limitBy: ['ISP_STORAGE', 'PP_STORAGE'],
    label: 'marketplaceResult.appType',
    options: new FilterOptions([
      {
        key: 'ONNX_WEB_APP',
        value: 'marketplaceExplorer.filterDefinitions.applicationType.option.onnxWeb',
      },
      {
        key: 'OPEN_VX_APP',
        value: 'marketplaceExplorer.filterDefinitions.applicationType.option.openVx',
      },
      {
        key: 'PY_PICASSO_APP',
        value: 'marketplaceExplorer.filterDefinitions.applicationType.option.pyAct',
      },
      {
        key: 'NATIVE_PYTHON',
        value: 'marketplaceExplorer.filterDefinitions.applicationType.option.python',
      },
      {
        key: 'CVEDIA_RT_APP',
        value: 'marketplaceExplorer.filterDefinitions.applicationType.option.cvediaRt',
      },
    ]),
    tooltip: intl.formatMessage({
      id: 'marketplaceExplorer.filterDefinitions.applicationType.tooltip',
    }),
  },
  {
    key: 'executionContainer',
    type: 'OPTIONS',
    limitBy: ['ISP_STORAGE', 'PP_STORAGE'],
    label: 'marketplaceResult.targetDevice',
    options: new FilterOptions([
      {
        key: 'BROWSER',
        value: 'marketplaceExplorer.filterDefinitions.executionContainer.option.browser',
      },
      {
        key: 'GSP',
        value: 'marketplaceExplorer.filterDefinitions.executionContainer.option.gsp',
      },
      {
        key: 'CLIENT',
        value:
          'marketplaceExplorer.filterDefinitions.executionContainer.option.externalClient',
      },
      {
        key: 'NODE',
        value: 'marketplaceExplorer.filterDefinitions.executionContainer.option.edgeNode',
      },
    ]),
  },
  {
    key: 'studioVersion',
    type: 'MULTIPLE',
    limitBy: ['ISP_STORAGE', 'PP_STORAGE'],
    label: 'marketplaceResult.studioVersion',
    options: new FilterOptions([
      {
        key: '1.5.1',
        value: 'marketplaceExplorer.filterDefinitions.studioVersion.option.151',
      },
      {
        key: '1.5.2',
        value: 'marketplaceExplorer.filterDefinitions.studioVersion.option.152',
      },
      {
        key: '1.5.3',
        value: 'marketplaceExplorer.filterDefinitions.studioVersion.option.153',
      },
      {
        key: '1.5.4',
        value: 'marketplaceExplorer.filterDefinitions.studioVersion.option.154',
      },
      {
        key: '1.6',
        value: 'marketplaceExplorer.filterDefinitions.studioVersion.option.16',
      },
    ]),
  },
  {
    key: 'tags',
    type: 'TAGS',
    limitBy: [],
    label: 'marketplaceResult.tagsFilter',
    tooltip: intl.formatMessage({
      id: 'marketplaceExplorer.filterDefinitions.tags.tooltip',
    }),
  },
];
